import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * One survey conversation's answers, statement by statement — History's conversation detail for a
 * survey VoiceAgent (2026-10-03, design §16.7.2 AS BUILT 2026-10-03).
 *
 * Read from `getConversationSurveyAnswer`: the answers in the questionnaire's order, each beside the
 * question as THAT conversation's own version worded it, in the language the answer was given in — the
 * questionnaire's text, never translated by the console. The instrument's code leads each line, the
 * answer is worded in the console's English (`answerLabel`), and an answer the interviewer said it was
 * unsure of says so. A test conversation's answers are labelled Test.
 *
 * Who may read it is the API's answer: an owner, an admin or a member. A viewer is refused (one
 * person's answers are not analytics) and the panel says so in words rather than showing nothing.
 */
import { useQuery } from "@tanstack/react-query";
import { Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { ApiError } from "@wetalk/api-client";

import {
  NO_QUESTION_TEXT,
  answerCellText,
  questionPlaceText,
  respondentStatusLabel,
} from "../../campaigns/survey-result-copy.js";
import { useGateway } from "../../../api/provider.js";
import { conversationSurveyAnswerQuery } from "../../../api/query.js";
import { apiErrorText } from "../../../api/api-error.js";

import "../../campaigns/survey-result.css";

export function ConversationSurveyAnswer({ conversationId }: { readonly conversationId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const read = useQuery(conversationSurveyAnswerQuery(gateway, conversationId));

  return (
    <>
      <p className="c-va-caps c-va-caps-history">
        {portalText("operations", "answers_b12fcedc")}{read.data?.isTest === true ? (
          <>
            {" "}
            <Tag tone="outline">{portalText("operations", "test_640ab2ba")}</Tag>
          </>
        ) : null}
      </p>
      {read.isError ? (
        read.error instanceof ApiError && read.error.status === 403 ? (
          <p className="c-va-history-summary">
            {portalText("operations", "your_role_reads_the_survey_s_totals_on_the_results_tab_not_51d8e762")}</p>
        ) : (
          <p className="c-note c-note-critical">
            {portalText("operations", "these_answers_could_not_be_loaded_so_none_are_shown_40265728")}{apiErrorText(read.error, "")}
          </p>
        )
      ) : read.data === undefined ? (
        <p className="c-note">{portalText("operations", "fetching_the_answers_d3130257")}</p>
      ) : read.data.answer.length === 0 ? (
        <p
          className="c-va-history-summary"
          title={read.data.outcome === "no_outcome" ? undefined : portalText("operations", "it_ended_value_9723e5c1", { v0: String(respondentStatusLabel(read.data.outcome).toLowerCase()) })}
        >
          {portalText("operations", "no_answers_recorded_e4dc9d32")}</p>
      ) : (
        <ol className="c-survey-answers" aria-label={portalText("operations", "answers_question_by_question_14b1ec06")}>
          {read.data.answer.map((one) => (
            <li className="c-survey-answer" key={one.questionCode}>
              <span className="c-survey-answer-question">
                <span className="c-survey-question-code">{one.instrumentCode}</span>
                <span lang={one.languageCode}>{one.text ?? NO_QUESTION_TEXT()}</span>
                <span className="c-survey-tally-note">{questionPlaceText(one)}</span>
              </span>
              <span className="c-survey-answer-value">
                {answerCellText(one)}
                {one.confident === false ? <span className="c-survey-tally-note">{portalText("operations", "not_sure_3410d5f4")}</span> : null}
              </span>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
